import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Check, Circle, Loader2, MessageSquarePlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATE = {
  completed:   { label: "Completed",       box: "border-emerald-200 bg-emerald-50/70", dot: "bg-emerald-500", Icon: Check,         icon: "text-emerald-600" },
  active:      { label: "In progress",     box: "border-blue-300 bg-blue-50 shadow-md shadow-blue-100", dot: "bg-blue-500", Icon: Loader2, icon: "text-blue-600" },
  stuck:       { label: "Stuck / delayed", box: "border-red-300 bg-red-50 shadow-md shadow-red-100", dot: "bg-red-500",    Icon: AlertTriangle, icon: "text-red-600" },
  not_started: { label: "Not started",     box: "border-dashed border-slate-300 bg-white text-slate-400", dot: "bg-slate-300", Icon: Circle, icon: "text-slate-300" },
};

function Node({ n, onEdit }) {
  const s = STATE[n.state];
  const hasTime = n.actual_days != null;
  return (
    <div className={`group relative w-44 shrink-0 rounded-lg border px-2.5 py-2 text-xs transition hover:-translate-y-0.5 ${s.box}`} data-testid={`flow-node-${n.key}`}>
      <div className="flex items-start gap-1.5">
        <s.Icon size={14} className={`mt-0.5 shrink-0 ${s.icon} ${n.state === "active" ? "animate-spin" : n.state === "stuck" ? "animate-pulse" : ""}`} />
        <span className={`flex-1 font-semibold leading-tight ${n.state === "not_started" ? "text-slate-500" : "text-slate-900"}`}>{n.label}</span>
        <button title="Record reason / change plan" className="text-slate-400 opacity-60 transition hover:text-slate-900 group-hover:opacity-100" onClick={() => onEdit(n)}>
          <MessageSquarePlus size={13} />
        </button>
      </div>
      {n.state !== "not_started" && (
        <div className="mt-1.5 font-mono-num text-[11px] pl-5">
          {hasTime ? (
            <>
              <b className="text-slate-900">{n.actual_days}d</b> <span className="text-slate-500" title={`Plan source: ${n.plan_source === "schedule" ? "worked out from the promised due date" : n.plan_source === "manual" ? "set by a supervisor" : "default for this step"}`}>/ plan {n.planned_days}d{n.plan_source === "schedule" ? "*" : ""}</span>{" "}
              {n.variance_days > 0 && <b className="text-red-600">+{n.variance_days}d</b>}
            </>
          ) : <span className="text-slate-400">plan {n.planned_days}d · no dates</span>}
        </div>
      )}
      {n.state === "not_started" && <div className="mt-1.5 pl-5 font-mono-num text-[11px]">plan {n.planned_days}d</div>}
      {n.reason_label && <div className="mt-1.5 ml-5 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-inset ring-red-200" title={n.note || ""}>{n.reason_label}</div>}
    </div>
  );
}

// Step-by-step pipeline for one order: 4 chapters, colour-coded nodes, planned vs actual days.
export default function OrderFlowchart({ jobId, asOf }) {
  const [flow, setFlow] = useState(null);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ reason_code: "", note: "", planned_days: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/dashboard/flow/${jobId}`, { params: asOf ? { as_of: asOf } : {} })
      .then(({ data }) => { setFlow(data); setError(""); })
      .catch((e) => setError(e?.response?.status === 404
        ? "Flowchart service not found. Restart the backend server so it picks up the new routes."
        : formatApiError(e)));
  }, [jobId, asOf]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (n) => {
    setEdit(n);
    setForm({ reason_code: n.reason_code || "", note: n.note || "", planned_days: String(n.planned_days) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (form.planned_days !== "" && Number(form.planned_days) !== edit.planned_days) body.planned_days = Number(form.planned_days);
      if (form.reason_code !== (edit.reason_code || "") || form.note !== (edit.note || "")) {
        body.reason_code = form.reason_code || null;
        body.note = form.note;
      }
      if (!Object.keys(body).length) { setEdit(null); return; }
      await api.post(`/dashboard/flow/${jobId}/${edit.key}`, body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="p-3 mt-3 text-xs border border-red-200 bg-red-50 text-red-800">
        {error} <button className="underline ml-2" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!flow) return <div className="p-4 text-xs text-neutral-500">Loading flowchart…</div>;

  const done = flow.nodes.filter((n) => n.state === "completed").length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="order-flowchart">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-600">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(done / flow.nodes.length) * 100}%` }} />
          </div>
          <b className="font-mono-num text-slate-900">{done} of {flow.nodes.length}</b> steps complete
        </div>
        <div className="ml-auto flex flex-wrap gap-4">
          {Object.values(STATE).map((s) => (
            <span key={s.label} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}</span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-4 w-max">
          {flow.chapters.map((c) => (
            <div key={c.key} className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{c.label}</div>
              <div className="flex items-center gap-1">
                {flow.nodes.filter((n) => n.chapter === c.key).map((n, i, arr) => (
                  <div key={n.key} className="flex items-center gap-1">
                    <Node n={n} onEdit={openEdit} />
                    {i < arr.length - 1 && <ArrowRight size={14} className="text-slate-300 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-neutral-500">
        Days are counted from the previous recorded date, so steps without their own date are folded into the next dated step. Plan days marked * are worked out from the promised due date; edit a step to set your own.
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit?.label}</DialogTitle></DialogHeader>
          <label className="text-xs">Reason code
            <select value={form.reason_code} onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                    className="mt-1 h-9 w-full border border-neutral-300 px-2 text-sm bg-white">
              <option value="">No reason</option>
              {flow.reason_codes.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </label>
          <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" />
          <label className="text-xs">Planned days for this step
            <Input type="number" min="0" step="0.5" value={form.planned_days} className="h-9 mt-1 w-32"
                   onChange={(e) => setForm({ ...form, planned_days: e.target.value })} />
          </label>
          {(flow.history[edit?.key] || []).length > 0 && (
            <div className="text-[11px] text-neutral-500 max-h-24 overflow-y-auto">
              {flow.history[edit.key].slice().reverse().map((h, i) => (
                <div key={i}>{h.at?.slice(0, 10)} · {h.by} · {h.reason_code || "-"} {h.note ? `· ${h.note}` : ""}</div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
